import React from 'react';
import { Elements } from '@/components/elements';
import { SkeletonPlansSection } from './SkeletonPlansSection';
import { formatCurrencyWithCommas, formatNumberWithCommas } from '@/utils/formatNumber';
import Delete from '@/assets/icons/Delete.svg';
import CustomPlanIcon from '@/assets/icons/customPlan.svg';
import Modal from '@/components/elements/Modal/Modal';
import { usePlansSectionState } from './usePlansSectionState';
import type { PlansSectionProps } from './types';
import Image from 'next/image';

export type { PlanWithAgePremiums, PlansSectionProps } from './types';

export const PlansSection = ({
    plans,
    isStateCountrySearch = false,
    familyAges = [],
    loading = false,
    activeTab,
}: PlansSectionProps) => {
    const {
        filteredPlans,
        paginatedPlans,
        totalPages,
        summaryData,
        hasTooManyFilters,
        plansByMetalLevel,
        plansByExchange,
        filters,
        page,
        pageSize,
        sortBy,
        comparePlans,
        isFiltersModalOpen,
        isCompareModalOpen,
        isCurrentPlanModalOpen,
        isCompareCurrentPlanLimitModalOpen,
        isComparePlansExpanded,
        isEmployerContributionExpanded,
        employerContributionInput,
        employerContributionAmount,
        setIsFiltersModalOpen,
        setIsCompareModalOpen,
        setIsCurrentPlanModalOpen,
        setIsCompareCurrentPlanLimitModalOpen,
        setIsComparePlansExpanded,
        setIsEmployerContributionExpanded,
        setEmployerContributionInput,
        setEmployerContributionAmount,
        setPage,
        parseMoney,
        formatMoneyInput,
        formatMoneyFixed2,
        toTitleCase,
        handleBackToMap,
        handleFiltersPanelChange,
        handleToggleComparePlan,
        handleRemoveComparePlan,
        handleAddCurrentPlanToCompare,
        handleChangePageSize,
        handlePrevPage,
        handleNextPage,
        handleShowMore,
        handleMetalFilterToggle,
        handleExchangeFilterToggle,
        handleSortByChange,
    } = usePlansSectionState({
        plans,
        isStateCountrySearch,
        familyAges,
        activeTab,
    });

    const ChevronDownIcon = () => (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.6004 7.4585L11.1671 12.8918C10.5254 13.5335 9.47539 13.5335 8.83372 12.8918L3.40039 7.4585" stroke="#A8AFB7" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const ChevronUpIcon = () => (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.39961 12.5415L8.83294 7.10817C9.47461 6.4665 10.5246 6.4665 11.1663 7.10817L16.5996 12.5415" stroke="#0077DF" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const ComparePlansCard = ({
        wrapperClassName = '',
        panelId,
    }: {
        wrapperClassName?: string;
        panelId: string;
    }) => {
        return (
            <div className={wrapperClassName}>
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden transition-colors hover:border-gray-300">
                    <button
                        type="button"
                        onClick={() => setIsComparePlansExpanded((v) => !v)}
                        className="flex w-full cursor-pointer items-center justify-between gap-2 h-[56px] px-5 py-2.5 hover:bg-gray-50"
                        aria-expanded={isComparePlansExpanded}
                        aria-controls={panelId}
                    >
                        <span className="text-base lg:text-xl font-medium text-gray-900">Compare Plans</span>
                        {isComparePlansExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </button>

                    {isComparePlansExpanded && (
                        <div id={panelId} className="px-5 py-3">
                            <div className="flex flex-col gap-2">
                                {comparePlans.map((plan) => {
                                    const isCurrentPlan = (plan as any).isCurrentPlan === true;
                                    const currentPlanTitle = isCurrentPlan ? toTitleCase(plan.display_name || plan.name) : '';
                                    const metal = String(plan.level || '').toLowerCase();
                                    const metalDotClass =
                                        metal.includes('catastrophic') ? 'bg-[#CC6076]' :
                                        metal.includes('bronze') ? 'bg-[#E59A42]' :
                                        metal.includes('silver') ? 'bg-[#A8AFB7]' :
                                        metal.includes('gold') ? 'bg-[#F6DB53]' :
                                        metal.includes('platinum') ? 'bg-[#8FADCF]' :
                                        'bg-gray-300';
                                    // Format premium: if age 18 and 65 exist, show as $X(18) to $Y(65)
                                    let premiumDisplay: React.ReactNode = '-';
                                    if (plan.premiumAge18 && plan.premiumAge65) {
                                        const premium18Str = String(plan.premiumAge18).trim();
                                        const premium65Str = String(plan.premiumAge65).trim();
                                        const premium18Num = parseFloat(premium18Str.replace(/[^0-9.]/g, '')) || 0;
                                        const premium65Num = parseFloat(premium65Str.replace(/[^0-9.]/g, '')) || 0;
                                        if (premium18Num > 0 && premium65Num > 0) {
                                            premiumDisplay = (
                                                <div className="flex items-center gap-1">
                                                    <span
                                                        className={`shrink-0 rounded-full ring-1 ring-black/10 ${metalDotClass}`}
                                                        style={{ width: 17, height: 17 }}
                                                        aria-label="Metal level"
                                                        title={plan.level}
                                                    />
                                                    <span className="text-sm text-brand-700">
                                                        {formatCurrencyWithCommas(premium18Num)}
                                                        <span className="text-gray-900"> (18)</span>
                                                        <span className="text-gray-900"> to </span>
                                                        {formatCurrencyWithCommas(premium65Num)}
                                                        <span className="text-gray-900"> (65)</span>
                                                    </span>
                                                </div>
                                            );
                                        }
                                    } else {
                                        const premiumStr = plan.premium != null ? String(plan.premium) : '';
                                        const premiumNum = parseFloat(premiumStr.replace(/[^0-9.]/g, '')) || 0;
                                        premiumDisplay =
                                            premiumNum > 0 ? (
                                                <div className="flex items-center gap-1">
                                                    <span
                                                        className={`shrink-0 rounded-full ring-1 ring-black/10 ${metalDotClass}`}
                                                        style={{ width: 17, height: 17 }}
                                                        aria-label="Metal level"
                                                        title={plan.level}
                                                    />
                                                    <span className="text-sm text-brand-700">{formatCurrencyWithCommas(premiumNum)}</span>
                                                </div>
                                            ) : (
                                                '-'
                                            );
                                    }

                                    return (
                                        <div key={plan.id} className="flex items-center gap-3 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2">
                                            {isCurrentPlan ? (
                                                <div className="h-10 w-10 rounded flex items-center justify-center">
                                                    <Image
                                                        src={CustomPlanIcon}
                                                        alt="Custom Plan"
                                                        className="h-10 w-10 rounded object-contain"
                                                        width={40}
                                                        height={40}
                                                    />
                                                </div>
                                            ) : plan.carrier.logo_url ? (
                                                <img
                                                    src={plan.carrier.logo_url}
                                                    alt={plan.carrier.name || 'Carrier logo'}
                                                    className="h-10 w-10 rounded object-contain"
                                                />
                                            ) : null}
                                            <div className="flex min-w-0 flex-1 flex-col">
                                                <span
                                                    className="truncate text-base font-normal text-gray-900"
                                                    title={
                                                        isCurrentPlan
                                                            ? currentPlanTitle || plan.display_name || plan.name || ''
                                                            : plan.display_name || plan.name || ''
                                                    }
                                                >
                                                    {isCurrentPlan ? currentPlanTitle || plan.display_name : plan.display_name}
                                                </span>
                                                {typeof premiumDisplay === 'string' ? (
                                                    <span className="text-sm text-brand-700">{premiumDisplay}</span>
                                                ) : (
                                                    premiumDisplay
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveComparePlan(plan.id)}
                                                className="flex cursor-pointer h-6 w-6 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                                aria-label="Remove from compare"
                                            >
                                                <Image src={Delete} alt="Delete" width={16} height={16} />
                                            </button>
                                        </div>
                                    );
                                })}
                                {Array.from({ length: Math.max(0, 3 - comparePlans.length) }).map((_, idx) => (
                                    <div
                                        key={idx}
                                        className="flex h-14 justify-center items-center gap-2 rounded-lg border border-dashed border-brand-200 bg-brand-50 px-3 py-2"
                                    />
                                ))}
                            </div>
                            <div className="mt-3">
                                <Elements.Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    className="w-full"
                                    disabled={comparePlans.length < 2}
                                    onClick={() => setIsCompareModalOpen(true)}
                                >
                                    {comparePlans.length < 2 ? 'Add 1 More to Start Compare' : `Compare`}
                                </Elements.Button>
                                <Elements.Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="w-full whitespace-normal text-sm md:text-base leading-snug py-2 text-center"
                                    onClick={() => {
                                        const nonCurrentCount = comparePlans.filter((p) => !(p as any).isCurrentPlan).length;
                                        if (nonCurrentCount >= 3) {
                                            setIsCompareCurrentPlanLimitModalOpen(true);
            return;
        }
                                        setIsCurrentPlanModalOpen(true);
                                    }}
                                >
                                    Compare to my current plan
                                </Elements.Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <section className="px-5 pb-12 md:px-10 w-full max-w-[1440px]" style={{ maxWidth: '1440px', margin: '0 auto' }}>
            {!loading && plans.length > 0 && (
                <div className="pt-5 bg-[#FFFFFF]">
                    {isStateCountrySearch && activeTab === 'state-county' && (
                        <div className="-mt-2 hidden lg:block">
                            <Elements.Button
                                type="button"
                                variant="link"
                                onClick={handleBackToMap}
                                className="flex items-center gap-2 text-brand-700 hover:text-brand-800 no-underline"
                            >
                                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                                    <path d="M6.38004 4.45337L2.33337 8.50004L6.38004 12.5467" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M13.6667 8.5H2.44666" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>Back to map</span>
                            </Elements.Button>
                        </div>
                    )}
                    <div className="mx-auto flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between lg:gap-4 w-full">
                        <Elements.Title
                            title={`${formatNumberWithCommas(filteredPlans.length)} Plans Available`}
                            type="h4"
                            className="text-left text-2xl font-semibold text-gray-900 md:text-2xl lg:text-2xl lg:shrink-0"
                        />

                        <div className="flex flex-wrap items-center gap-1 md:gap-2 lg:flex-1 lg:justify-end">
                            <Elements.CheckBoxToggle
                                label={
                                    <span className="flex items-center gap-1.5">
                                        <span className="text-sm md:text-base font-medium text-gray-600">Catastrophic</span>
                                        <span className="text-sm md:text-base font-semibold text-white px-1 py-0.5 rounded-md bg-[#CC6076] min-w-6 text-center">
                                            {plansByMetalLevel.Catastrophic}
                                        </span>
                                    </span>
                                }
                                selected={filters.metal.includes('Catastrophic')}
                                onToggle={() => handleMetalFilterToggle('Catastrophic')}
                                variant="catastrophic"
                            />
                            <Elements.CheckBoxToggle
                                label={
                                    <span className="flex items-center gap-1.5">
                                        <span className="text-sm md:text-base font-medium text-gray-600">Bronze</span>
                                        <span className="text-sm md:text-base font-semibold text-white px-1 py-0.5 rounded-md bg-[#E59A42] min-w-6 text-center">
                                            {plansByMetalLevel.Bronze}
                                        </span>
                                    </span>
                                }
                                selected={filters.metal.includes('Bronze')}
                                onToggle={() => handleMetalFilterToggle('Bronze')}
                                variant="bronze"
                            />
                            <Elements.CheckBoxToggle
                                label={
                                    <span className="flex items-center gap-1.5">
                                        <span className="text-sm md:text-base font-medium text-gray-600">Silver</span>
                                        <span className="text-sm md:text-base font-semibold text-white px-1 py-0.5 rounded-md bg-[#A8AFB7] min-w-6 text-center">
                                            {plansByMetalLevel.Silver}
                                        </span>
                                    </span>
                                }
                                selected={filters.metal.includes('Silver')}
                                onToggle={() => handleMetalFilterToggle('Silver')}
                                variant="silver"
                            />
                            <Elements.CheckBoxToggle
                                label={
                                    <span className="flex items-center gap-1.5">
                                        <span className="text-sm md:text-base font-medium text-gray-600">Gold</span>
                                        <span className="text-sm md:text-base font-semibold text-white px-1 py-0.5 rounded-md bg-[#F6DB53] min-w-6 text-center">
                                            {plansByMetalLevel.Gold}
                                        </span>
                                    </span>
                                }
                                selected={filters.metal.includes('Gold')}
                                onToggle={() => handleMetalFilterToggle('Gold')}
                                variant="gold"
                            />
                            <Elements.CheckBoxToggle
                                label={
                                    <span className="flex items-center gap-1.5">
                                        <span className="text-sm md:text-base font-medium text-gray-600">Platinum</span>
                                        <span className="text-sm md:text-base font-semibold text-white px-1 py-0.5 rounded-md bg-[#8FADCF] min-w-6 text-center">
                                            {plansByMetalLevel.Platinum}
                                        </span>
                                    </span>
                                }
                                selected={filters.metal.includes('Platinum')}
                                onToggle={() => handleMetalFilterToggle('Platinum')}
                                variant="platinum"
                            />
                            <Elements.CheckBoxToggle
                                label={
                                    <span className="flex items-center gap-1.5">
                                        <span className="text-sm md:text-base font-medium text-gray-600">On-Exchange</span>
                                        <span className="text-sm md:text-base font-semibold text-primary-700 px-1 py-0.5 rounded-md bg-[#C0E0F7] min-w-6 text-center">
                                            {plansByExchange.onExchange}
                                        </span>
                                    </span>
                                }
                                selected={filters.planMarket.includes('On-Exchange')}
                                onToggle={() => handleExchangeFilterToggle('On-Exchange')}
                                variant="marketBlue"
                            />
                            <Elements.CheckBoxToggle
                                label={
                                    <span className="flex items-center gap-1.5">
                                        <span className="text-sm md:text-base font-medium text-gray-600">Off-Exchange</span>
                                        <span className="text-sm md:text-base font-semibold text-[#14A557] px-1 py-0.5 rounded-md bg-[#DCFCEA] min-w-6 text-center">
                                            {plansByExchange.offExchange}
                                        </span>
                                    </span>
                                }
                                selected={filters.planMarket.includes('Off-Exchange')}
                                onToggle={() => handleExchangeFilterToggle('Off-Exchange')}
                                variant="marketGreen"
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className="mx-auto md:mt-6">
                {loading ? (
                    <div className="transition-opacity duration-300 ease-in-out" style={{ opacity: loading ? 1 : 0 }}>
                        <SkeletonPlansSection isStateCountrySearch={isStateCountrySearch} />
                    </div>
                ) : (
                    <div
                        className="mb-8 grid grid-cols-1 gap-6 md:gap-4 md:grid-cols-[434px_254px] lg:grid-cols-[1fr_443px] md:items-start transition-opacity duration-300 ease-in-out"
                        style={{ opacity: loading ? 0 : 1 }}
                    >
                        <div className="w-full max-w-[434px] md:max-w-[434px] lg:max-w-[909px]">
                            {hasTooManyFilters ? (
                                <div className="w-full rounded-xl border border-transparent bg-brand-50 p-15 text-center shadow-sm">
                                    <h3 className="text-3xl font-medium text-gray-900 mb-2">Too Many Filters</h3>
                                    <p className="text-lg text-gray-900">Please adjust to see plans</p>
                                </div>
                            ) : (
                                <>
                            {!isStateCountrySearch && (
                                        <div className="flex flex-col gap-6 md:gap-4">
                                <Elements.PlansSummaryCard
                                    averageBronze={summaryData.average.Bronze || '-'}
                                    averageSilver={summaryData.average.Silver || '-'}
                                    averageGold={summaryData.average.Gold || '-'}
                                    averagePlatinum={summaryData.average.Platinum || '-'}
                                    averageCatastrophic={summaryData.average.Catastrophic || '-'}
                                    lowestBronze={summaryData.lowest.Bronze || '-'}
                                    lowestSilver={summaryData.lowest.Silver || '-'}
                                    lowestGold={summaryData.lowest.Gold || '-'}
                                    lowestPlatinum={summaryData.lowest.Platinum || '-'}
                                    lowestCatastrophic={summaryData.lowest.Catastrophic || '-'}
                                />

                                            {/* Mobile: Compare Plans should be above Employer Contribution */}
                                            <ComparePlansCard wrapperClassName="md:hidden" panelId="compare-plans-panel-mobile" />

                                            {/* Mobile: Employer Contribution lives under the summary card */}
                                            {activeTab === 'age-zip' && (
                                                <div className="md:hidden flex flex-col gap-6 md:gap-4 rounded-xl border border-gray-200 bg-white overflow-hidden transition-colors hover:border-gray-300">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsEmployerContributionExpanded((v) => !v)}
                                                        className="flex w-full cursor-pointer items-center justify-between gap-2 h-[56px] px-5 py-2.5 hover:bg-gray-50"
                                                        aria-expanded={isEmployerContributionExpanded}
                                                        aria-controls="employer-contribution-panel-mobile"
                                                    >
                                                        <span className="text-base lg:text-xl font-medium text-gray-900">Employer Contribution</span>
                                                        {isEmployerContributionExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                                    </button>

                                                    {isEmployerContributionExpanded && (
                                                        <div id="employer-contribution-panel-mobile" className="px-5 pb-5">
                                                            <div className="mt-1 flex flex-col items-start gap-3 lg:flex-row lg:items-center lg:justify-between">
                                                                <div className="text-sm lg:text-base font-normal text-gray-900">Monthly Contribution Amount</div>
                                                                <div className="w-full lg:w-[150px]">
                                                                    <Elements.Input
                                                                        id="employerContributionAmount"
                                                                        type="text"
                                                                        placeholder="0.00"
                                                                        prefix="$"
                                                                        value={employerContributionInput}
                                                                        onChange={(e) => setEmployerContributionInput(formatMoneyInput(e.target.value))}
                                                                        onBlur={(e) => setEmployerContributionInput(formatMoneyInput(e.target.value))}
                                                                        inputWrapClassName="w-full"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <Elements.Button
                                                                type="button"
                                                                variant="primary"
                                                                outline
                                                                size="sm"
                                                                className="mt-4 w-full text-sm lg:text-base"
                                                                onClick={() => {
                                                                    const amount = parseMoney(employerContributionInput);
                                                                    if (amount > 0) {
                                                                        setEmployerContributionAmount(amount);
                                                                        setEmployerContributionInput(formatMoneyFixed2(amount));
                                                                    } else {
                                                                        setEmployerContributionAmount(null);
                                                                        setEmployerContributionInput('');
                                                                    }
                                                                }}
                                                            >
                                                                Set Employer Contribution
                                                            </Elements.Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                            )}

                            {filteredPlans.length > 0 && (
                                        <div className={`${!isStateCountrySearch ? 'mt-6' : ''} flex flex-col gap-6 md:gap-4 w-full max-w-[434px] md:max-w-[434px] lg:max-w-[909px]`}>
                                    {paginatedPlans.map((plan) => {
                                        const planIndex = comparePlans.findIndex((p) => p.id === plan.id);
                                        const isCompared = planIndex !== -1;
                                        const comparePosition = isCompared ? planIndex + 1 : undefined;
                                        return (
                                        <Elements.PlanCard
                                                key={`${plan.id}-${isCompared}-${comparePosition}`}
                                            plan={plan}
                                            isStateCountrySearch={isStateCountrySearch}
                                            familyAges={familyAges}
                                            employerContributionAmount={employerContributionAmount}
                                            cta={{ kind: 'choose', label: 'Choose Plan', action: (plan) => console.log('Choose plan:', plan) }}
                                                onToggleCompare={() => handleToggleComparePlan(plan)}
                                                isCompared={isCompared}
                                                comparePosition={comparePosition}
                                                compareTotal={3}
                                        />
                                        );
                                    })}
                                </div>
                                    )}
                                </>
                            )}

                            {filteredPlans.length > 0 && !hasTooManyFilters && (
                                <div className="mt-6 md:hidden">
                                    {pageSize < 50 && filteredPlans.length > pageSize && (
                                        <button
                                            type="button"
                                            onClick={handleShowMore}
                                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            Waive
                                        </button>
                                    )}
                                </div>
                            )}

                            {filteredPlans.length > 0 && !hasTooManyFilters && (
                                <div className="mt-6 hidden md:flex items-center justify-between border-t border-gray-200 bg-white px-3 py-3">
                                    {/* Left: Plans per page */}
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-600">Plans per page</span>
                                            <select
                                                className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm"
                                                value={pageSize}
                                                onChange={handleChangePageSize}
                                            >
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </select>
                                        </div>

                                    {/* Center: Show More button - only show when pageSize < 50 */}
                                    <div className="flex-1 flex justify-center">
                                        {pageSize < 50 && (
                                            <button
                                                type="button"
                                                onClick={handleShowMore}
                                                className="rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                            >
                                                Show More
                                            </button>
                                        )}
                                    </div>

                                    {/* Right: Pagination */}
                                        <div className="flex items-center gap-2">
                                            <button
                                                className="rounded-lg px-2 py-1 ring-1 ring-gray-200 disabled:opacity-50"
                                                aria-label="Prev"
                                                onClick={handlePrevPage}
                                                disabled={page === 1}
                                            >
                                                ‹
                                            </button>

                                            <select
                                                className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm"
                                                value={page}
                                            onChange={(e) => {
                                                setPage(Number(e.target.value));
                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                            }}
                                            >
                                                {Array.from({ length: totalPages }, (_, i) => (
                                                    <option key={i} value={i + 1}>
                                                        Page {i + 1}
                                                    </option>
                                                ))}
                                            </select>

                                            <button
                                                className="rounded-lg px-2 py-1 ring-1 ring-gray-200 disabled:opacity-50"
                                                aria-label="Next"
                                                onClick={handleNextPage}
                                                disabled={page === totalPages}
                                            >
                                                ›
                                            </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="order-first md:order-0">
                            <div className="flex flex-col gap-6 md:gap-4">
                                {/* Desktop: Employer Contribution should be above Compare Plans */}
                                {activeTab === 'age-zip' && (
                                    <div className="hidden md:flex flex-col gap-6 md:gap-4 rounded-xl border border-gray-200 bg-white overflow-hidden transition-colors hover:border-gray-300">
                                        <button
                                            type="button"
                                            onClick={() => setIsEmployerContributionExpanded((v) => !v)}
                                            className="flex w-full cursor-pointer items-center justify-between gap-2 h-[56px] px-5 py-2.5 hover:bg-gray-50"
                                            aria-expanded={isEmployerContributionExpanded}
                                            aria-controls="employer-contribution-panel-desktop"
                                        >
                                            <span className="text-base lg:text-xl font-medium text-gray-900">Employer Contribution</span>
                                            {isEmployerContributionExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                        </button>

                                        {isEmployerContributionExpanded && (
                                            <div id="employer-contribution-panel-desktop" className="px-5 pb-5">
                                                <div className="mt-3 flex flex-col items-start gap-3 lg:flex-row lg:items-center lg:justify-between">
                                                    <div className="text-sm lg:text-base font-normal text-gray-900">Monthly Contribution Amount</div>
                                                    <div className="w-full lg:w-[150px]">
                                                        <Elements.Input
                                                            id="employerContributionAmount"
                                                            type="text"
                                                            placeholder="0.00"
                                                            prefix="$"
                                                            value={employerContributionInput}
                                                            onChange={(e) => setEmployerContributionInput(formatMoneyInput(e.target.value))}
                                                            onBlur={(e) => setEmployerContributionInput(formatMoneyInput(e.target.value))}
                                                            inputWrapClassName="w-full"
                                                        />
                                                    </div>
                                                </div>

                                                <Elements.Button
                                                    type="button"
                                                    variant="primary"
                                                    outline
                                                    size="sm"
                                                    className="mt-4 w-full text-sm lg:text-base"
                                                    onClick={() => {
                                                        const amount = parseMoney(employerContributionInput);
                                                        if (amount > 0) {
                                                            setEmployerContributionAmount(amount);
                                                            setEmployerContributionInput(formatMoneyFixed2(amount));
                                                        } else {
                                                            setEmployerContributionAmount(null);
                                                            setEmployerContributionInput('');
                                                        }
                                                    }}
                                                >
                                                    Set Employer Contribution
                                                </Elements.Button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <ComparePlansCard
                                    wrapperClassName="hidden md:block"
                                    panelId="compare-plans-panel-sidebar"
                                />

                                <div className="hidden md:block">
                                <Elements.SortByDropdown value={sortBy} onChange={handleSortByChange} />
                            </div>
                            <div className="hidden md:block">
                                <Elements.FiltersPanel plans={plans} currentFilters={filters} onFiltersChange={handleFiltersPanelChange} />
                                </div>
                            </div>
                        </div>

                        {isFiltersModalOpen && (
                            <div className="fixed inset-0 z-70 flex items-end">
                                <div className="fixed inset-0 bg-black/50" onClick={() => setIsFiltersModalOpen(false)}></div>
                                <div className="relative w-full h-[80vh] rounded-t-3xl bg-white flex flex-col overflow-hidden">
                                    <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-200 shrink-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
                                        </div>
                                        <button
                                            onClick={() => setIsFiltersModalOpen(false)}
                                            className="size-10 cursor-pointer flex items-center justify-center"
                                            aria-label="Close modal"
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18 6L6 18M6 6L18 18" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                                        <Elements.FiltersPanel
                                            plans={plans}
                                            currentFilters={filters}
                                            onFiltersChange={handleFiltersPanelChange}
                                            isModal={true}
                                        />
                                    </div>
                                    <div className="w-full bg-white border-t border-gray-200 p-4 shrink-0" style={{ boxShadow: '0px -4px 8.4px 0px #00000014' }}>
                                        <Elements.Button
                                            type="button"
                                            variant="primary"
                                            size="md"
                                            onClick={() => setIsFiltersModalOpen(false)}
                                            className="w-full"
                                        >
                                            Apply
                                        </Elements.Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <Elements.CompareModal
                isOpen={isCompareModalOpen}
                onClose={() => setIsCompareModalOpen(false)}
                comparePlans={comparePlans}
                familyAges={familyAges}
                isStateCountrySearch={isStateCountrySearch}
                employerContributionAmount={employerContributionAmount}
                onToggleComparePlan={handleToggleComparePlan}
            />
            <Elements.CompareCurrentPlanModal
                isOpen={isCurrentPlanModalOpen}
                onClose={() => setIsCurrentPlanModalOpen(false)}
                onAddToCompare={handleAddCurrentPlanToCompare}
                disableAdd={comparePlans.filter((p) => !(p as any).isCurrentPlan).length >= 3}
            />

            <Modal
                isOpen={isCompareCurrentPlanLimitModalOpen}
                onClose={() => setIsCompareCurrentPlanLimitModalOpen(false)}
                size="md"
                title="Compare to my current plan"
                actions={
                    <Elements.Button type="button" variant="primary" onClick={() => setIsCompareCurrentPlanLimitModalOpen(false)}>
                        OK
                    </Elements.Button>
                }
            >
                <div className="text-base text-gray-700">
                    You must remove 1 of the other plans being compared before adding your current plan to the comparison tool
                </div>
            </Modal>
        </section>
    );
};

